/*

  riddlePersistence.ts

  Local persistence for Gáta Dagsins using localStorage

  Copyright (C) 2025 Miðeind ehf.

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { IPlayerMove } from './types';
import { getFirebaseData } from './channel';

interface LocalRiddleData {
  userId: string; // User ID to ensure data belongs to the correct user
  date: string; // ISO format: YYYY-MM-DD, "2025-01-26"
  moves: IPlayerMove[]; // Complete move history
  timestamp: string; // ISO timestamp
}

// Firebase read-only data structures
interface UserRiddleStats {
  currentStreak: number; // Current consecutive days played
  longestStreak: number; // Best ever consecutive days
  topScoreStreak: number; // Current consecutive days with perfect score
  lastPlayedDate: string; // ISO date of last play
  totalDaysPlayed: number; // Total number of days played
  totalTopScores: number; // Total perfect scores achieved
}

interface LeaderboardEntry {
  userId: string;
  displayName: string;
  score: number;
  timestamp: string;
}

export class RiddlePersistence {
  private static STORAGE_KEY_PREFIX = 'gata_';
  private static MAX_AGE_DAYS = 30;

  // Generate storage key for a specific user and date
  private static getStorageKey(userId: string, date: string): string {
    return `${this.STORAGE_KEY_PREFIX}${date}_${userId}`;
  }

  // Save complete move list to localStorage
  static saveLocalMoves(userId: string, date: string, moves: IPlayerMove[]): void {
    if (!userId || !date) {
      return;
    }

    const data: LocalRiddleData = {
      date,
      moves,
      timestamp: new Date().toISOString(),
      userId,
    };

    try {
      const key = this.getStorageKey(userId, date);
      localStorage.setItem(key, JSON.stringify(data));

      // Clean up old entries while we're here
      this.cleanupOldEntries();
    } catch (e) {
      // Handle localStorage quota errors silently
      console.error('Failed to save moves to localStorage:', e);
    }
  }

  // Load move history from localStorage
  static loadLocalMoves(userId: string, date: string): IPlayerMove[] {
    if (!userId || !date) {
      return [];
    }
    try {
      const key = this.getStorageKey(userId, date);
      const stored = localStorage.getItem(key);
      if (!stored) {
        return [];
      }
      const data: LocalRiddleData = JSON.parse(stored);
      // Verify that this data belongs to the correct (current) user
      if (!data.userId || data.userId !== userId) {
        return [];
      }
      return data.moves || [];
    } catch (e) {
      console.error('Failed to load moves from localStorage:', e);
      return [];
    }
  }

  // Check if user has achieved top score (local check)
  static hasAchievedTopScore(userId: string, date: string, topScore: number): boolean {
    const moves = this.loadLocalMoves(userId, date);
    return moves.some(move => move.score >= topScore);
  }

  // Get the best move from localStorage
  static getBestLocalMove(userId: string, date: string): IPlayerMove | null {
    const moves = this.loadLocalMoves(userId, date);
    if (moves.length === 0) {
      return null;
    }
    // Find the move with the highest score
    return moves.reduce((best, current) =>
      current.score > best.score ? current : best
    );
  }

  // Clean up entries older than MAX_AGE_DAYS
  private static cleanupOldEntries(): void {
    try {
      const now = new Date();
      const cutoffDate = new Date(now);
      cutoffDate.setDate(cutoffDate.getDate() - this.MAX_AGE_DAYS);

      const keysToRemove: string[] = [];

      // Iterate through localStorage keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.STORAGE_KEY_PREFIX)) {
          // Extract date from key: "gata_YYYY-MM-DD_userId"
          const parts = key.split('_');
          if (parts.length >= 2) {
            const dateStr = parts[1];
            const entryDate = new Date(dateStr);
            if (!isNaN(entryDate.getTime()) && entryDate < cutoffDate) {
              keysToRemove.push(key);
            }
          }
        }
      }

      // Remove old entries
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (e) {
      console.error('Failed to cleanup old entries:', e);
    }
  }

  // Clear all persistence for a specific user
  static clearUserData(userId: string): void {
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.STORAGE_KEY_PREFIX) && key.endsWith(`_${userId}`)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));
  }

  // === Firebase Read Methods ===
  // Note: All Firebase write operations (achievements, stats, global best, leaderboard)
  // are now handled by the backend server in the /gatadagsins/submit endpoint.
  // The client only handles localStorage persistence and Firebase reads for display.

  // Get leaderboard for a specific date
  static async getLeaderboard(
    date: string,
    locale: string,
    limit: number = 10
  ): Promise<LeaderboardEntry[]> {
    try {
      const leadersPath = `gatadagsins/${date}/${locale}/leaders`;
      const leaders = await getFirebaseData(leadersPath);

      if (!leaders) {
        return [];
      }

      // Convert object to array and sort by score
      const entries: LeaderboardEntry[] = Object.values(leaders);
      entries.sort((a, b) => b.score - a.score);

      return entries.slice(0, limit);
    } catch (error) {
      console.error('Failed to get leaderboard:', error);
      return [];
    }
  }

  // Get user's streak statistics
  static async getUserStats(userId: string, locale: string): Promise<UserRiddleStats | null> {
    try {
      const statsPath = `gatadagsins/users/${locale}/${userId}/stats`;
      const stats = await getFirebaseData<UserRiddleStats>(statsPath);
      return stats;
    } catch (error) {
      console.error('Failed to get user stats:', error);
      return null;
    }
  }
}
